<script setup>
import { storeToRefs } from 'pinia'
import ValueCard from 'assets/icons/valuecard.svg'
import { useMessageStore } from '~/stores/messages'
import { useModalStore } from '~/stores/modal'
import { PARKING_CONSTANTS } from '~/utils/parkingConstants'

const props = defineProps({
    parkingOverviewValueCardConfig: {
        type: Object,
        default: () => ({}),
        validator: (rawValue) => {
            return rawValue.productTypes === undefined || Array.isArray(rawValue.productTypes)
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

const hasMultiPurposeCardFeature = computed(() => props.parkingOverviewValueCardConfig?.productTypes?.includes(PARKING_CONSTANTS.MULTI_PURPOSE_CARD))

// Markdown
const { $md } = useNuxtApp()
const parkingValueCardDescription = computed(() => $md.renderInline(staticKeys.value?.parkingfirstpage_valuecard_copytext || ''))
const parkingTicketDescription = computed(() => $md.renderInline(staticKeys.value?.parkingfirstpage_valuecard_copytext_parkingticket || ''))

const description = computed(() => hasMultiPurposeCardFeature.value ? parkingValueCardDescription.value : parkingTicketDescription.value)

// Modal store
const modalStore = useModalStore()
const { toggleModal, addModalContent } = modalStore

const createValueCardButtonConfig = computed(() => {
    return {
        buttonType: 'secondary',
        type: 'div',
        large: true,
        active: true,
        fn: () => clickCreateValueCardButton()
    }
})

const headlineConfigValueCard = computed(() => {
    return {
        headline: {
            title: hasMultiPurposeCardFeature.value ? staticKeys.value?.parkingfirstpage_valuecard_headline : staticKeys.value?.parkingfirstpage_parkingticket_headline,
            type: 'h2'
        },
        icon: {
            name: ValueCard,
            size: 'large'
        }
    }
})

const appButtonsConfig = computed(() => {
    return {
        iosLink: staticKeys.value?.account_gettheapp_ios_link_content,
        androidLink: staticKeys.value?.account_gettheapp_android_link_content
    }
})

const parkingAppContent = computed(() => {
    return {
        title: staticKeys.value?.parkingfirstpage_valuecard_app_info_headline_web,
        description: staticKeys.value?.parkingfirstpage_valuecard_app_info_web_markdown
    }
})

/**
 * Handles the click event on the create value card button.
 *
 * @return {void}
 */
const clickCreateValueCardButton = () => {
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.PARKING_HOME_CLICK,
        button: TRACKING_CONSTANTS.MISC.PARKING_CREATE_PREPAID_CARD
    })

    addModalContent(parkingAppContent.value)
    toggleModal(true)
}
</script>

<template>
    <ContentMainHeadline :content-main-headline-config="{ title: `_${staticKeys?.parkingfirstpage_headline}_` }" />

    <PremiumContentBox>
        <template #content>
            <DefaultContentBoxHeadline :content-box-headline-config="headlineConfigValueCard" />

            <div class="parking-value-card-description">
                <p v-html="description" />
            </div>

            <div class="parking-value-card-action-cta">
                <BaseButton v-if="hasMultiPurposeCardFeature" :base-button-config="createValueCardButtonConfig">
                    {{ staticKeys?.parkingfirstpage_valuecard_create_cta }}
                </BaseButton>
            </div>

            <h4 class="parking-value-card-subline">
                {{ staticKeys.parkingfirstpage_valuecard_app_subline }}
            </h4>

            <AppDownloadButtonBar
                class="parking-value-card-btn-container"
                :app-download-button-bar-config="appButtonsConfig"
            />
        </template>
    </PremiumContentBox>
</template>

<style lang="scss">
.parking-value-card-description {
    p {
        margin-block-end: 20px;

        @include body1-medium;

        @include bp(medium1) {
            margin-block-end: 40px;
        }
    }

    ul {
        margin-inline-start: 20px;
        padding: 0;
        margin-block-end: 20px;

        @include body1-medium;

        @include bp(medium1) {
            margin-block-end: 40px;
        }

        li {
            list-style-type: disc;
            margin: 0 0 7px;
            padding: 0;
        }
    }
}

.parking-value-card-subline {
    display: none;
    width: 100%;
    color: $parking-value-card-subline;
    text-align: center;
    margin-block-end: px2rem(20);

    @include body3-extrabold;

    @include bp(large1) {
        display: block;
    }
}

.parking-value-card-btn-container {
    display: none;
    align-items: center;
    justify-content: center;
    flex-flow: row wrap;

    @include bp(large1) {
        display: flex;
    }

    .android-link,
    .ios-link {
        margin-inline: px2rem(8);
    }
}

.parking-value-card-action-cta {
    display: flex;
    justify-content: center;

    @include bp(large1) {
        display: none;
    }
}
</style>
